import * as THREE from 'three/webgpu';
import {
  clamp,
  color,
  exp,
  float,
  Fn,
  mix,
  mx_noise_float,
  mx_worley_noise_vec3,
  positionGeometry,
  texture,
  time,
  vec2,
  vec3,
  vec4,
  type ShaderNodeObject,
} from 'three/tsl';

export const windNoiseFunction = /*@__PURE__*/ Fn(
  ([noiseTexture, worldPosition]: [
    THREE.Texture,
    THREE.TSL.ShaderNodeObject<THREE.Node>
  ]) => {
    const direction = vec2(-1, 1).normalize();

    // compute noise uv
    const noiseUv1 = worldPosition.xy
      .mul(0.06)
      .add(direction.mul(time.mul(0.1))).xy;
    const noise1 = texture(noiseTexture, noiseUv1).r.sub(0.5);

    const nouseUv2 = worldPosition.xy
      .mul(0.043)
      .add(direction.mul(time.mul(0.03))).xy;
    const noise2 = texture(noiseTexture, nouseUv2).r;

    const intensity = noise1.mul(noise2);

    return vec2(direction.mul(intensity));
  }
);

export const worleyNoiseFunction = /*@__PURE__*/ Fn(
  ([noiseScale, noiseSeed, depth]: [
    THREE.ConstNode<number>,
    THREE.ConstNode<number>,
    THREE.ConstNode<number>
  ]) => {
    const pos = positionGeometry
      .mul(vec3(1, 1, exp(noiseScale)))
      .add(noiseSeed)
      .toVar();

    const worly = mx_worley_noise_vec3(pos);

    const fWorl = worly.x.pow(float(depth).mul(1)).smoothstep(0.7, 0);

    return fWorl;
  }
);

function noised(
  pos: ShaderNodeObject<THREE.Node>,
  scale = 1,
  octave = 1,
  seed = 0
) {
  return mx_noise_float(pos.mul(scale, octave).add(seed));
}

export const simplexNoise = /*@__PURE__*/ Fn(
  ([scale, seed, contrast, balance, background, colorN]: [
    ShaderNodeObject<THREE.Node>,
    ShaderNodeObject<THREE.Node>,
    ShaderNodeObject<THREE.Node>,
    ShaderNodeObject<THREE.Node>,
    ShaderNodeObject<THREE.Node>,
    ShaderNodeObject<THREE.Node>
  ]) => {
    const pos: ShaderNodeObject<THREE.Node> = positionGeometry
      .mul(exp(scale))
      .add(seed);

    const k = clamp(
      0,
      1,
      noised(pos).mul(0.5, exp(contrast)).add(0.5, balance)
    );

    return mix(color(background), color(colorN), k);
  }
);

// Helper functions
const permute = Fn(([x]: [ShaderNodeObject<THREE.Node>]) => {
  return x.mul(34.0).add(1.0).mul(x).mod(289.0);
});

const taylorInvSqrt = Fn(([r]: [ShaderNodeObject<THREE.Node>]) => {
  return float(1.79284291400159).sub(r.mul(0.85373472095314));
});

const grad4 = Fn(
  ([j, ip]: [ShaderNodeObject<THREE.Node>, ShaderNodeObject<THREE.Node>]) => {
    const ones = vec4(1.0, 1.0, 1.0, -1.0);
    const p = vec4().toVar();
    const s = vec4().toVar();

    // p.xyz = floor(fract(vec3(j) * ip.xyz) * 7.0) * ip.z - 1.0
    p.xyz.assign(
      vec3(j).mul(ip.xyz).fract().mul(7.0).floor().mul(ip.z).sub(1.0)
    );

    // p.w = 1.5 - dot(abs(p.xyz), ones.xyz)
    p.w.assign(float(1.5).sub(p.xyz.abs().dot(ones.xyz)));

    // s = vec4(lessThan(p, vec4(0.0)))
    s.assign(p.lessThan(vec4(0.0)).toFloat());

    // p.xyz = p.xyz + (s.xyz*2.0 - 1.0) * s.www
    p.xyz.assign(p.xyz.add(s.xyz.mul(2.0).sub(1.0).mul(s.www)));

    return p;
  }
);

export const simplexNoise4d = Fn(([v]: [ShaderNodeObject<THREE.Node>]) => {
  // Constants
  const C = vec2(0.138196601125010504, 0.309016994374947451);

  // First corner
  const i = v.add(v.dot(C.yyyy)).floor().toVar();
  const x0 = v.sub(i).add(i.dot(C.xxxx)).toVar();

  // Other corners - Rank sorting
  const i0 = vec4().toVar();

  const isX = x0.yzw.step(x0.xxx).toVar();
  const isYZ = x0.zww.step(x0.yyz).toVar();

  i0.x.assign(isX.x.add(isX.y).add(isX.z));
  i0.yzw.assign(float(1.0).sub(isX));

  i0.y.addAssign(isYZ.x.add(isYZ.y));
  i0.zw.addAssign(float(1.0).sub(isYZ.xy));

  i0.z.addAssign(isYZ.z);
  i0.w.addAssign(float(1.0).sub(isYZ.z));

  // i0 now contains the unique values 0,1,2,3 in each channel
  const i3 = i0.clamp(0.0, 1.0).toVar();
  const i2 = i0.sub(1.0).clamp(0.0, 1.0).toVar();
  const i1 = i0.sub(2.0).clamp(0.0, 1.0).toVar();

  // Calculate x1, x2, x3, x4
  const x1 = x0.sub(i1).add(C.xxxx.mul(1.0)).toVar();
  const x2 = x0.sub(i2).add(C.xxxx.mul(2.0)).toVar();
  const x3 = x0.sub(i3).add(C.xxxx.mul(3.0)).toVar();
  const x4 = x0.sub(1.0).add(C.xxxx.mul(4.0)).toVar();

  // Permutations
  i.assign(i.mod(289.0));

  const j0 = permute(
    permute(permute(permute(i.w).add(i.z)).add(i.y)).add(i.x)
  ).toVar();

  const j1 = permute(
    permute(
      permute(
        permute(i.w.add(vec4(i1.w, i2.w, i3.w, 1.0))).add(
          i.z.add(vec4(i1.z, i2.z, i3.z, 1.0))
        )
      ).add(i.y.add(vec4(i1.y, i2.y, i3.y, 1.0)))
    ).add(i.x.add(vec4(i1.x, i2.x, i3.x, 1.0)))
  ).toVar();

  // Gradients
  const ip = vec4(1.0 / 294.0, 1.0 / 49.0, 1.0 / 7.0, 0.0);

  const p0 = grad4(j0, ip).toVar();
  const p1 = grad4(j1.x, ip).toVar();
  const p2 = grad4(j1.y, ip).toVar();
  const p3 = grad4(j1.z, ip).toVar();
  const p4 = grad4(j1.w, ip).toVar();

  // Normalize gradients
  const norm = taylorInvSqrt(
    vec4(p0.dot(p0), p1.dot(p1), p2.dot(p2), p3.dot(p3))
  ).toVar();

  p0.mulAssign(norm.x);
  p1.mulAssign(norm.y);
  p2.mulAssign(norm.z);
  p3.mulAssign(norm.w);
  p4.mulAssign(taylorInvSqrt(p4.dot(p4)));

  // Mix contributions from the five corners
  const m0 = float(0.6)
    .sub(vec3(x0.dot(x0), x1.dot(x1), x2.dot(x2)))
    .max(0.0)
    .toVar();
  const m1 = float(0.6)
    .sub(vec2(x3.dot(x3), x4.dot(x4)))
    .max(0.0)
    .toVar();

  m0.assign(m0.mul(m0));
  m1.assign(m1.mul(m1));

  return float(49.0).mul(
    m0
      .mul(m0)
      .dot(vec3(p0.dot(x0), p1.dot(x1), p2.dot(x2)))
      .add(m1.mul(m1).dot(vec2(p3.dot(x3), p4.dot(x4))))
  );
});
